import './kp-media-viewer-viewer.less';

import type {ViewableFile} from 'typings/portaro.be.types';
import {ngAsync, ngComponentCssClass} from 'shared/utils/ng-@decorators';
import type {Component} from 'typings/portaro.fe.types';
import type SearchApi from '../search-api';
import type {Ace} from 'ace-builds';
import {Range} from 'ace-builds';
import type {IDeferred, IOnChanges, IPostLink, IPromise, IQService} from 'angular';
import {FileViewForm} from 'typings/file-view-form';

/**
 * @ngdoc type
 * @name ViewMode
 * @module portaro.media-viewer
 *
 * @description
 * Type of possible view modes display content in {@link component:kpMediaViewerViewer}.
 *
 * Possible values are: `'ORIGINAL'`, `'TEXTUAL'`.
 */
export type ViewMode = 'ORIGINAL' | 'TEXTUAL';

export abstract class KpMediaViewerViewerComponentController implements KpMediaViewerViewerComponentBindings, IPostLink, IOnChanges {
    public file: ViewableFile;
    public editMode = false;
    public viewMode: ViewMode;
    public currentPage: number;
    public showBrowsingButtons: boolean;

    public abstract next(locals: { $onNext: IPromise<never> }): boolean;

    public abstract previous(locals: { $onPrevious: IPromise<never> }): boolean;

    public abstract searchApi(locals: { $api: SearchApi });

    public abstract onCurrentPageChange(locals: { $currentPage: number });

    public abstract onPageCountChange(locals: { $pageCount: number });

    public fileEditor: Ace.Editor;
    public searchQuery: string = null;
    public onNextDeferred: IDeferred<never>;
    public onPreviousDeferred: IDeferred<never>;
    private cachedPrevious: boolean | null = null;
    private cachedNext: boolean | null = null;

    /*@ngInject*/
    constructor(public CONTEXT_PATH: string, private $q: IQService) {
    }

    public $postLink(): void {
        this.searchApi({
            $api: {
                search: this.search.bind(this),
                previous: this.searchPrevious.bind(this),
                next: this.searchNext.bind(this),
                cancel: this.cancelSearch.bind(this)
            }
        });
    }

    public $onChanges(): void {
        this.cachedPrevious = this.cachedNext = null;
    }

    /*
        TODO
        Zatim nechavam zakomentovane, protoze pri prvnim nacteni to funguje spatne.
        Problem je v tom, ze media-viewer-model.ts:getCurrentIdx spatne vyhodnocuje currentIndex pri prvnim nacteni
        To se postupne dostane i do cache, ktera ma spatnou hodnotu
        Az se ten problem vyresi, nize zakomentovane pouziti cache
     */
    public hasNext(): boolean {
        /* if (this.cachedNext !== null) {
            return this.cachedNext;
        } else {
            this.onNextDeferred = this.$q.defer();
            this.cachedNext = this.next({$onNext: this.onNextDeferred.promise});

            return this.cachedNext;
        } */

        this.onNextDeferred = this.$q.defer();
        return this.next({$onNext: this.onNextDeferred.promise});
    }

    public hasPrevious(): boolean {
        /* if (this.cachedPrevious !== null) {
            return this.cachedPrevious;
        } else {
            this.onPreviousDeferred = this.$q.defer();
            this.cachedPrevious = this.previous({$onPrevious: this.onPreviousDeferred.promise});

            return this.cachedPrevious;
        } */

        this.onPreviousDeferred = this.$q.defer();
        return this.previous({$onPrevious: this.onPreviousDeferred.promise});
    }

    public currentPageChanged(currentPage: number) {
        this.currentPage = currentPage;
        this.onCurrentPageChange({$currentPage: currentPage});
    }

    // eslint-disable-next-line @typescript-eslint/require-await
    @ngAsync()
    private async search(query: string): Promise<number> {
        this.searchQuery = query;

        if (this.fileEditor) {
            this.fileEditor.find(query, {});
            return (this.fileEditor.getValue().match(new RegExp(query, 'g')) || []).length;
        }

        return 0;
    }

    // eslint-disable-next-line @typescript-eslint/require-await
    @ngAsync()
    private async searchPrevious(): Promise<number> {
        if (this.fileEditor) {
            this.fileEditor.findPrevious();

            return this.getWordLastIndexFromString(this.getTextFromBeginningToCursorPosition(), this.searchQuery);
        }
    }

    // eslint-disable-next-line @typescript-eslint/require-await
    @ngAsync()
    private async searchNext(): Promise<number> {
        if (this.fileEditor) {
            this.fileEditor.findNext();

            return this.getWordLastIndexFromString(this.getTextFromBeginningToCursorPosition(), this.searchQuery);
        }
    }

    private cancelSearch() {
        this.searchQuery = null;
    }

    private getTextFromBeginningToCursorPosition(): string {
        const cursorPosition = this.fileEditor.getCursorPosition();
        return this.fileEditor.getSession().getTextRange(new Range(0, 0, cursorPosition.row, cursorPosition.column));
    }

    private getWordLastIndexFromString(input: string, word: string): number {
        return (input.match(new RegExp(word, 'g')) || []).length - 1;
    }

    public get isImage(): boolean {
        return this.file.viewForms.includes(FileViewForm.IMAGE);
    }

    public get isPdf(): boolean {
        return this.file.viewForms.includes(FileViewForm.PDF);
    }

    public get isMedia(): boolean {
        return this.file.viewForms.includes(FileViewForm.MEDIA);
    }
}

/**
 * @ngdoc component
 * @name kpMediaViewerViewer
 * @module portaro.media-viewer
 *
 * @param {ViewableFile} file File to display.
 * @param {ViewMode} viewMode Mode of displaying file. If it has textual form, it can be displayed.
 * @param {function(IPromise): boolean} [previous] Previous file function. See description.
 * @param {Promise<never>} previous.$onPrevious Promise fulfilled if previous button is pressed.
 * @param {function(IPromise): boolean} [next] Next file function. See description.
 * @param {Promise<never>} next.$onNext Promise fulfilled if next button is pressed.
 * @param {boolean=} editMode Is edit mode enabled?
 * @param {number=} currentPage Current page, if document is pageable.
 * @param {boolean} showBrowsingButtons Show previous and next arrow buttons.
 *
 * @param {function()} [searchApi] Connection to search api.
 * @param {SearchApi} [searchApi.$api] Search API.
 * @param {function()} [onCurrentPageChange] Page change callback.
 * @param {number} [onCurrentPageChange.$currentPage] Page change callback.
 * @param {function()} [onPageCountChange] Page count.
 * @param {number} [onPageCountChange.$pageCount] Current page number.
 *
 * @description
 * Component for viewing file.
 *
 * If file is image, it display zoomable image, if it is text, it displays text.
 *
 * If `editMode` is enabled, textual file can be edited via text editor.
 *
 * If `editMode` is disabled, it can show browsing arrows for next and previous file.
 *
 * `next` and `previous` parameters are functions, which returns boolean result depends on availability (hasNext, hasPrevious) and consumes promise, which
 * will be fulfilled on next/prev button press.
 *
 * @example
 * ```html
 *      <kp-media-viewer-viewer file="$ctrl.currentFile" previous="$ctrl.hasPrevious($onPrevious)" next="$ctrl.hasNext($onNext)"></kp-media-viewer-viewer>
 * ```
 *
 * ```js
 *      class Controller {
 *
 *          constructor() {
 *              // only symbolic
 *              this.files = [file1, file2, file3];
 *              this.currentFile = this.files[0];
 *          }
 *
 *          hasNext($onNext) {
 *              $onNext.then(() => {
 *                  this.currentFile = this.files[this.getCurrentFileIndex() + 1];
 *              });
 *
 *              return this.getCurrentFileIndex() < this.files.length - 1;
 *          }
 *
 *          hasPrevious($onPrevious) {
 *              $onPrevious.then(() => {
 *                  this.currentFile = this.files[this.getCurrentFileIndex() - 1];
 *              });
 *
 *              return this.getCurrentFileIndex() > 0;
 *          }
 *
 *          getCurrentFileIndex() {
 *              return this.files.indexOf(this.currentFile);
 *          }
 *
 *      }
 * ```
 */
// eslint-disable-next-line max-classes-per-file
export default class KpMediaViewerViewerComponent implements Component<KpMediaViewerViewerComponentBindings> {
    public static componentName = 'kpMediaViewerViewer';

    @ngComponentCssClass('media-viewer-viewer')
    public templateUrl = require('./kp-media-viewer-viewer.tpl.ftl');

    public bindings = {
        file: '<',
        editMode: '<',
        viewMode: '<',
        currentPage: '<',
        next: '&',
        previous: '&',
        searchApi: '&',
        showBrowsingButtons: '<',
        onCurrentPageChange: '&',
        onPageCountChange: '&'
    } as const;

    public controller = KpMediaViewerViewerComponentController;
}

interface KpMediaViewerViewerComponentBindings {
    file: ViewableFile;
    editMode: boolean;
    viewMode: ViewMode;
    currentPage: number;
    showBrowsingButtons: boolean;

    next(locals: { $onNext: IPromise<never> }): boolean;

    previous(locals: { $onPrevious: IPromise<never> }): boolean;

    searchApi(locals: { $api: SearchApi });

    onCurrentPageChange(locals: { $currentPage: number });

    onPageCountChange(locals: { $pageCount: number });
}
